import React, { useState } from "react";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Calendar } from "react-date-range";
import { enUS, fi } from "date-fns/locale";
import Select from "react-select";
import { useMutation } from "@apollo/client";
import {
  CREATE_DRIVER_LICENSE,
  UPDATE_KYC,
  UPDATE_USER,
} from "../../apollo/operations";
import { showToast } from "../../utils/showToast";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { ClipLoader } from "react-spinners";
import { LocationSelect } from "../../components";
import { useNavigate } from "react-router-dom";
import { calculateAge } from "../../utils/daysBetweenDates";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { ref } from 'firebase/storage';

const genders = ["Male", "Female", "Other"] as const;

const genderArr = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

// Array of all U.S. states
const states = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const VerificationSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  dateOfBirth: z.date({
    required_error: "Date of birth is required",
  }),
  driversLicenseNumber: z
    .string()
    .min(1, "Driver's license number is required"),
  issueState: z.object({
    label: z.string(),
    value: z.string(),
  }),
  issueDate: z.date({
    required_error: "Issue date is required",
  }),
  expiryDate: z.date({
    required_error: "Expiry date is required",
  }),
  address: z.string().min(1, "Address is required"),
  gender: z.object({
    label: z.string(),
    value: z.string(),
  }),
});

type IdVerificationForm = z.infer<typeof VerificationSchema>;

const VerificationForm = () => {
  const navigate = useNavigate();
  const [showDob, setShowDob] = useState(false);
  const [showIssueDate, setShowIssueDate] = useState(false);
  const [showExpiryDate, setShowExpiryDate] = useState(false);
  const user = useSelector((state: RootState) => state.user.userDetails);
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<IdVerificationForm>({
    resolver: zodResolver(VerificationSchema),
  });

  const [inputData, setInputData] = useState<IdVerificationForm | null>(null);

  const issueDateRef = React.useRef(null);
  const expiryDateRef = React.useRef(null);

  useOutsideClick(issueDateRef, () => {
    if (showIssueDate) {
      setShowIssueDate(false);
    }
  });

  useOutsideClick(expiryDateRef, () => {
    if (showExpiryDate) {
      setShowExpiryDate(false);
    }
  });
  const [createDriverLicense, { loading: licenseLoading }] = useMutation(
    CREATE_DRIVER_LICENSE,
    {
      onCompleted: (data) => {
        // console.log(data);
        updateUser({
          variables: {
            where: {
              id: user?.id,
            },
            data: {
              firstName: inputData?.firstName,
              lastName: inputData?.lastName,
            },
          },
        });
      },
      onError: (error) => {
        const errorMessage = error.graphQLErrors[0]?.extensions?.originalError;
        console.log(error);
        console.log(errorMessage);
        showToast((errorMessage as string) ?? error.message, "error");
      },
    }
  );

  const [updateKYC, { loading: kycLoading }] = useMutation(UPDATE_KYC, {
    onCompleted(data) {
      showToast("ID Verification Successful", "success");
      setTimeout(() => {
        navigate("/dashboard");
      }, 3000);
      // showToast("ID Verification Successful", "success");
    },
    onError(error) {
      console.log(error);
      const errorMessage = error.graphQLErrors[0]?.extensions?.originalError;
      showToast((errorMessage as string) ?? error.message, "error");
    },
  });

  const [updateUser, { loading: updateLoading }] = useMutation(UPDATE_USER, {
    onCompleted: (data) => {
      updateKYC({
        variables: {
          where: {
            id: user?.id,
          },
          data: {
            userOnboardingStatus: "VerifyDriverLicense",
          },
        },
      });
    },
    onError: (error) => {
      console.log(error);
      const errorMessage = error.graphQLErrors[0]?.extensions?.originalError;
      showToast((errorMessage as string) ?? error.message, "error");
    },
  });

  const onSubmit = (data: IdVerificationForm) => {
    // console.log(data);
    setInputData(data);
    const age = calculateAge(data.dateOfBirth.toISOString());

    if (age < 21) {
      showToast("You must be 21 years or older to verify your ID", "error");
      return;
    }
    if (licenseLoading || updateLoading || kycLoading) return;
    createDriverLicense({
      variables: {
        data: {
          address: data.address,
          dateOfBirth: data.dateOfBirth,
          expiryDate: data.expiryDate,
          gender: data.gender.value,
          issuedDate: data.issueDate,
          issuedState: data.issueState.value,
          licenseNumber: data.driversLicenseNumber,
          name: data.firstName + " " + data.lastName,
          user: {
            id: user?.id,
          },
        },
      },
    });
  };
  return (
    <div className="w-full py-[100px] lg:py-[110px] bg-[#F6F7F9]">
      <div className="flex w-[80%] lg:w-[70%] py-10 border rounded-2xl mx-auto justify-center  h-full  flex-col gap-2 lg:items-center  bg-white">
        <p className=" text-lightGreen pl-3 text-left font-Poppins font-semibold lg:font-medium text-[18px] md:text-[24px] lg:text-[40px]  ">
          Driver's License Verification
        </p>
        <div className="flex flex-col  gap-7 w-[90%] mx-auto  lg:w-2/3 ">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className=" flex gap-2 items-center flex-col lg:flex-row">
              <div className=" w-full lg:w-1/2">
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700"
                >
                  First Name
                </label>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <input
                      id="firstName"
                      {...field}
                      className="mt-1 block w-full rounded-md border shadow-sm text-base h-12 px-2 active:outline-none focus:outline-none focus:ring-2 focus:ring-lightGreen/20 focus:border-transparent border-darkGreen/20"
                    />
                  )}
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.firstName.message}
                  </p>
                )}
              </div>
              <div className="w-full lg:w-1/2">
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Last Name
                </label>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <input
                      id="lastName"
                      {...field}
                      className="mt-1 block w-full rounded-md border shadow-sm text-base h-12 px-2 active:outline-none focus:outline-none focus:ring-2 focus:ring-lightGreen/20 focus:border-transparent border-darkGreen/20"
                    />
                  )}
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.lastName.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="dateOfBirth"
                className="block text-sm font-medium text-gray-700"
              >
                Date of Birth
              </label>
              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field }) => (
                  <div
                    id="dateOfBirth"
                    onClick={() => {
                      setShowDob(!showDob);
                      setShowIssueDate(false);
                      setShowExpiryDate(false);
                    }}
                    className="mt-1 flex items-center w-full rounded-md border shadow-sm text-base h-12 px-2 active:outline-none focus:outline-none focus:ring-2 focus:ring-lightGreen/20 focus:border-transparent border-darkGreen/20"
                  >
                    {field.value
                      ? format(field.value, "MM-dd-yyyy")
                      : "Select Date of Birth"}
                  </div>
                )}
              />
              <div className="w-full flex justify-start ">
                {showDob && (
                  <Calendar
                    date={new Date()}
                    // minDate={new Date()}
                    onChange={(date) => {
                      setValue("dateOfBirth", date);
                      setShowDob(false);
                    }}
                    locale={enUS}
                  />
                )}
              </div>
              {errors.dateOfBirth && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.dateOfBirth.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="driversLicenseNumber"
                className="block text-sm font-medium text-gray-700"
              >
                Driver's License Number
              </label>
              <Controller
                name="driversLicenseNumber"
                control={control}
                render={({ field }) => (
                  <input
                    id="driversLicenseNumber"
                    {...field}
                    className="mt-1 block w-full rounded-md border shadow-sm text-base h-12 px-2 active:outline-none focus:outline-none focus:ring-2 focus:ring-lightGreen/20 focus:border-transparent border-darkGreen/20"
                  />
                )}
              />
              {errors.driversLicenseNumber && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.driversLicenseNumber.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="issueState"
                className="block text-sm font-medium text-gray-700"
              >
                Issue State
              </label>
              <Controller
                name="issueState"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={states}
                    placeholder="Select Issue State"
                    className="mt-1 block w-full rounded-md border shadow-sm text-base h-  active:outline-none focus:outline-none focus:ring-2 focus:ring-lightGreen/20 focus:border-transparent border-darkGreen/20"
                  />
                )}
              />
              {errors.issueState && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.issueState.message}
                </p>
              )}
            </div>
            <div className=" flex gap-2 items-center flex-col lg:flex-row">
              <div className="w-full lg:w-1/2 relative" ref={issueDateRef}>
                <label
                  htmlFor="issueDate"
                  className="block text-sm font-medium text-gray-700"
                >
                  Issue Date
                </label>
                <Controller
                  name="issueDate"
                  control={control}
                  render={({ field }) => (
                    <div
                      id="issueDate"
                      onClick={() => {
                        setShowDob(false);
                        setShowIssueDate(!showIssueDate);
                        setShowExpiryDate(false);
                      }}
                      className="mt-1 flex items-center w-full rounded-md border shadow-sm text-base h-12 px-2 active:outline-none focus:outline-none focus:ring-2 focus:ring-lightGreen/20 focus:border-transparent border-darkGreen/20"
                    >
                      {field.value
                        ? format(field.value, "MM-dd-yyyy")
                        : "Select Issue Date"}
                    </div>
                  )}
                />
                <div className="w-full flex justify-start absolute mt-4 z-[99] ">
                  {showIssueDate && (
                    <Calendar
                      date={new Date()}
                      // minDate={new Date()}
                      onChange={(date) => {
                        setValue("issueDate", date);
                        setShowIssueDate(false);
                      }}
                      locale={enUS}
                    />
                  )}
                </div>
                {errors.issueDate && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.issueDate.message}
                  </p>
                )}
              </div>

              <div className="w-full lg:w-1/2 relative" ref={expiryDateRef}>
                <label
                  htmlFor="expiryDate"
                  className="block text-sm font-medium text-gray-700"
                >
                  Expiry Date
                </label>
                <Controller
                  name="expiryDate"
                  control={control}
                  render={({ field }) => (
                    <div
                      id="expiryDate"
                      onClick={() => {
                        setShowDob(false);
                        setShowIssueDate(false);
                        setShowExpiryDate(!showExpiryDate);
                      }}
                      className="mt-1 flex items-center w-full rounded-md border shadow-sm text-base h-12 px-2 active:outline-none focus:outline-none focus:ring-2 focus:ring-lightGreen/20 focus:border-transparent border-darkGreen/20"
                    >
                      {field.value
                        ? format(field.value, "MM-dd-yyyy")
                        : "Select Expiry Date"}
                    </div>
                  )}
                />
                <div className="w-full flex justify-start  absolute mt-4 z-[99]  ">
                  {showExpiryDate && (
                    <Calendar
                      date={new Date()}
                      minDate={new Date()}
                      onChange={(date) => {
                        setValue("expiryDate", date);
                        setShowExpiryDate(false);
                      }}
                      locale={enUS}
                    />
                  )}
                </div>
                {errors.expiryDate && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.expiryDate.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="address"
                className="block text-sm font-medium text-gray-700"
              >
                Address
              </label>
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  // <input
                  //   id="address"
                  //   {...field}
                  //   className="mt-1 block w-full rounded-md border shadow-sm text-base h-12 px-2 active:outline-none focus:outline-none focus:ring-2 focus:ring-lightGreen/20 focus:border-transparent border-darkGreen/20"
                  // />
                  <LocationSelect
                    inputId="license-location"
                    mapId="license-map"
                    handleSelectLocation={(lat, lng, location) => {
                      field.onChange(location);
                    }}
                    showMap={false}
                    extraStyles="border rounded-md px-5 lg:px-2 bg-transparent border-gray-300 text-[15px] h-12  text-greyText font-Poppins py-3"
                  />
                )}
              />
              {errors.address && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.address.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="gender"
                className="block text-sm font-medium text-gray-700"
              >
                Gender
              </label>
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={genderArr}
                    placeholder="Select Issue State"
                    className="mt-1 block w-full rounded-md border shadow-sm text-base h-  active:outline-none focus:outline-none focus:ring-2 focus:ring-lightGreen/20 focus:border-transparent border-darkGreen/20"
                  />
                )}
              />
              {errors.gender && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.gender.message}
                </p>
              )}
            </div>
            <div>
              <button
                type="submit"
                className="mt-3 ml-auto w-[250px] inline-flex gap-2 justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-darkGreen hover:bg-midGreen focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lightGreen"
              >
                <ClipLoader
                  color="white"
                  loading={licenseLoading || updateLoading || kycLoading}
                  size={20}
                />
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VerificationForm;
